import { useMemo } from 'react';
import { usePage } from '@inertiajs/react';
import { shortNameFor } from '@/utils/countryShortName';
import { isEuMember } from '@/utils/initCountries';

export type Country = App.Data.CountryData & { shortName: string };

type CountryMap = Record<string, App.Data.CountryData>;

declare global {
    interface Window {
        __init?: { countries?: CountryMap };
    }
}

/**
 * Reads the active countries map from the Inertia shared prop when inside
 * an Inertia tree, otherwise from `window.__init.countries` bootstrapped
 * by the master layout (see ViewServiceProvider + layouts/app.blade.php).
 *
 * This makes the hook safe to use from non-Inertia React mounts (Chef
 * SPA, Chef Onboarding) without coupling every consumer to context shape.
 */
function useCountryMap(): CountryMap {
    try {
        const inertia = usePage().props.init?.countries as CountryMap | undefined;
        if (inertia) return inertia;
    } catch {
        // Not inside an Inertia provider; fall through to window bootstrap.
    }
    return window.__init?.countries ?? {};
}

/**
 * Returns the active countries map keyed by ISO code.
 *
 * Each entry is augmented with a `shortName` field suitable for
 * prose ("the UK" / "the US"), defaulting to `name` for other markets.
 *
 * Replaces the previous hand-maintained `supportedCountries` list.
 */
export function useCountries(): Record<string, Country> {
    const countries = useCountryMap();
    
    return useMemo(
        () => Object.fromEntries(
            Object.entries(countries).map(([iso, c]) => [
                iso,
                { ...c, shortName: shortNameFor(c.name) },
            ]),
        ),
        [countries],
    );
}

/**
 * Returns the ISO codes of every active country.
 * Replaces ad-hoc `supportedCountries.map(c => c.value)` patterns.
 */
export function useActiveIsoCodes(): string[] {
    const countries = useCountries();
    return useMemo(() => Object.keys(countries), [countries]);
}

/**
 * Returns the ISO codes of active EU-member countries.
 * Uses the shared `isEuMember` predicate so UK is correctly excluded
 * despite carrying `region = 'EU'`.
 */
export function useEUIsoCodes(): string[] {
    const countries = useCountries();
    return useMemo(
        () => Object.keys(countries).filter((iso) =>
            isEuMember(iso, countries[iso].region),
        ),
        [countries],
    );
}
